/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { AttendanceStateDto } from '../../../../api-service/clients';
import { FasitDataGridToolbar } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { formatDate } from '../../../../utilities';
import { formatTime, getHoursAndMinutes } from '../../utils';
import { AttendanceStatisticsResultTableDetails } from './attendance-statistics-result-table-details';
import { getAttendanceStateTextColor, getStatisticsStateColor } from '../../components/attendance-protocol/utils';
import { AttendanceStatisticsLesson } from '../interfaces';

interface IAttendanceStatisticsResultTable {
  lessons: AttendanceStatisticsLesson[];
  showLessonDetails: boolean;
}

function CustomToolbar() {
  return FasitDataGridToolbar({
    title: 'Godkendte lektioner',
    showColumnButton: false,
    showDensityButton: false,
    showFilterButton: false,
  });
}

export function AttendanceStatisticsResultTable(props: IAttendanceStatisticsResultTable) {
  const { lessons, showLessonDetails } = props;

  const columns: GridColDef[] = [
    {
      field: 'courseName',
      headerName: 'Holdnavn',
      flex: 1,
    },
    {
      field: 'beginAt',
      headerName: 'Lektionsdato',
      width: 150,
      valueFormatter: params => formatDate(params.value),
    },
    {
      field: 'startTime',
      headerName: 'Starttidspunkt',
      width: 150,
      valueGetter: params => params.row.beginAt,
      valueFormatter: params => formatTime(params.value),
    },
    {
      field: 'endAt',
      headerName: 'Sluttidspunkt',
      width: 150,
      valueFormatter: params => formatTime(params.value),
    },
    { field: 'expectedTimeInMinutes', headerName: 'Forventet deltagelse', width: 150, valueFormatter: ({ value }) => getHoursAndMinutes(value) },
    {
      field: 'actualTimeInMinutes',
      headerName: 'Faktisk deltagelse',
      width: 150,
      cellClassName: ({ row }) => getTimeCellClass(row),
      valueFormatter: ({ value }) => getHoursAndMinutes(value),
    },
  ];

  return (
    <DataGridPro<AttendanceStatisticsLesson>
      columns={columns}
      rows={lessons || []}
      autoHeight
      disableSelectionOnClick
      getRowId={(row: AttendanceStatisticsLesson) => row.lessonId}
      density="compact"
      components={{
        Toolbar: CustomToolbar,
      }}
      getDetailPanelContent={showLessonDetails ? ({ row }) => renderLessonDetails(row) : undefined}
      getDetailPanelHeight={() => 'auto'}
      sx={{
        '& .status-ontime': {
          backgroundColor: getStatisticsStateColor(AttendanceStateDto.OnTime),
          color: getAttendanceStateTextColor(AttendanceStateDto.OnTime),
        },
        '& .status-notontime': {
          backgroundColor: getStatisticsStateColor(AttendanceStateDto.NotOnTime),
          color: getAttendanceStateTextColor(AttendanceStateDto.NotOnTime),
        },
        '& .status-notshown': {
          backgroundColor: getStatisticsStateColor(AttendanceStateDto.NotShown),
          color: getAttendanceStateTextColor(AttendanceStateDto.NotShown),
        },
        '& .status-absencewithreason': {
          backgroundColor: getStatisticsStateColor(AttendanceStateDto.AbsenceWithReason),
          color: getAttendanceStateTextColor(AttendanceStateDto.AbsenceWithReason),
        },
        '& .status-notrelevant': {
          backgroundColor: getStatisticsStateColor(AttendanceStateDto.NotRelevant),
          color: getAttendanceStateTextColor(AttendanceStateDto.NotRelevant),
        },
      }}
    />
  );
}

function renderLessonDetails(row: AttendanceStatisticsLesson) {
  const lesson = asCitizenLesson(row);
  return lesson ? <AttendanceStatisticsResultTableDetails lesson={lesson} /> : null;
}

function asCitizenLesson(row: AttendanceStatisticsLesson) {
  return 'category' in row ? row : undefined;
}

function getTimeCellClass(row: AttendanceStatisticsLesson) {
  switch (asCitizenLesson(row)?.category) {
    case AttendanceStateDto.OnTime:
      return 'status-ontime';
    case AttendanceStateDto.NotShown:
      return 'status-notshown';
    case AttendanceStateDto.AbsenceWithReason:
      return 'status-absencewithreason';
    case AttendanceStateDto.NotOnTime:
      return 'status-notontime';
    case AttendanceStateDto.NotRelevant:
      return 'status-notrelevant';
    default:
      return '';
  }
}
